import React, { useState, useMemo } from 'react'
import toast from 'react-hot-toast'
import { useForm, Controller } from 'react-hook-form'
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PanCardFormComponent from 'components/kyc/PanCardFormComponent'
import AppTextFieldComponent from 'components/mui/AppTextFieldComponent'
import DatePicker from 'components/common/DatePicker'
import Container from 'components/common/Container'
import Stack from 'components/mui/Stack'
import PanKycInfoModal from 'components/common/modal/PanKycInfoModal'
import FatcaDetailsModal from 'components/common/modal/FatcaDetailsModal'
import UpdateContactDetailsModal from 'components/common/modal/UpdateContactDetailsModal'
import PanFetchErrorModal from 'components/common/modal/PanFetchErrorModal'
import { ConfirmationModel } from 'components/common/modal/ConfirmationModel'
import FetchingDetailModal from 'components/common/modal/FetchingDetailModal'
import LoadingModal from 'components/common/modal/LoadingModal'
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ROUTES from 'router/routes.constants';
import { useInitiateKycMutation, useLazyGetKraDetailsQuery, useInitiateMobileEmailUpdateMutation, useReKycMutation, useCompleteKycMutation } from 'store/api/kyc.apislice';
import { useGetCustomerProfileQuery, useUpdateCustomerProfileMutation } from 'store/api/customer.apislice';
import { buildUpdateKycProfilePayload } from 'store/parsers/customer.parsers';
import { sanitizeIncomeSlabValue } from 'utils/kyc-fatca.utils';
import Box from 'components/mui/Box'


// Component scope - Constants and static data
/**
 * Income slab as the backend knows it, across every shape it can arrive in.
 * An empty result means BE could not supply it and FATCA details must be asked for.
 */
const resolveIncomeSlab = (profile, panData) => {
  const fatcaDetails = profile?.data?.fatcaDetails ?? {}
  // "00" from BE means the slab is not set, so ignore it and keep looking
  const value =
    sanitizeIncomeSlabValue?.(fatcaDetails?.incomeSlab) ||
    sanitizeIncomeSlabValue?.(profile?.profile?.annualIncome) ||
    sanitizeIncomeSlabValue?.(panData?.income) ||
    ''
  return typeof value === 'string' ? value.trim() : value ?? ''
}

const PAN_CARD_FORM_CONFIG = {
  title: 'KYC',
  subtitle: 'Please provide the following details to complete your KYC verification.',
  sectionTitle: 'PAN Card',
  sectionDescription: 'KYC is mandatory for investing in India. Your PAN helps us verify your KYC status instantly.',
  noteText: 'Note: We only use your PAN to verify your KYC. Rest assured, no investment account will be opened without your consent.',
  showNote: true,
  buttonText: 'Verify PAN'
}

const VALIDATION_RULES = {
  panNumber: {
    required: 'PAN number is required',
    pattern: {
      value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
      message: 'Invalid PAN format'
    }
  },
  dateOfBirth: {
    required: 'Date of birth is required',
    validate: (value) => {
      if (!value) return true;
      const parts = value?.split?.('-')?.map?.(Number) ?? []
      const [year, month, day] = parts
      if (!year || !month || !day) return 'Invalid date of birth'
      const selectedDate = new Date(year, month - 1, day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return selectedDate <= today || 'Date of birth cannot be in the future';
    }
  }
}

const DEFAULT_VALUES = {
  panNumber: '',
  dateOfBirth: ''
}

const INPUT_STYLES = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#2A2D3A',
    height: '56px',
    '& fieldset': {
      borderColor: '#3F4252'
    },
    '&:hover fieldset': {
      borderColor: '#1455FF'
    },
    '&.Mui-focused fieldset': {
      borderColor: '#1455FF'
    }
  },
  '& .MuiInputLabel-root': {
    color: 'white',
    fontSize: '14px',
    fontWeight: 400,
    transform: 'translate(14px, -9px) scale(0.75)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)'
    }
  },
  '& .MuiInputLabel-asterisk': {
    color: 'red'
  },
  '& .MuiInputBase-input': {
    color: 'white',
    fontSize: '14px'
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#808080',
    opacity: 1
  }
}

/**
 * PAN Card Container - Business logic for PAN card form
 * @component
 * @returns {React.ReactElement} PanCardContainer
 */
export const PanCardContainer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme?.breakpoints?.down?.('md'));
  // Modal state management
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)
  const [isFetchingModalOpen, setIsFetchingModalOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isFatcaModalOpen, setIsFatcaModalOpen] = useState(false)
  const [isUpdateContactModalOpen, setIsUpdateContactModalOpen] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [formData, setFormData] = useState(null)

  const [panResponseData, setPanResponseData] = useState(null)
  const navigate = useNavigate();
  const [initiateKyc, { isLoading: isKycLoading }] = useInitiateKycMutation();

  const [getKraDetails, { isFetching: isKraFetching }] = useLazyGetKraDetailsQuery();
  const [initiateMobileEmailUpdate] = useInitiateMobileEmailUpdateMutation();
  const [reKyc] = useReKycMutation();
  const [completeKyc, { isLoading: isCompleteKycLoading }] = useCompleteKycMutation();
  const [updateCustomerProfile, { isLoading: isUpdatingProfile }] = useUpdateCustomerProfileMutation();
  const { data: profileResponse, refetch: refetchProfile } = useGetCustomerProfileQuery();
  // Form setup with react-hook-form
  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    mode: 'onChange'
  })
  const { handleSubmit, control, formState } = form ?? {}
  const { errors, isValid } = formState ?? {}

  // Event handlers
  const handleFormSubmit = async (data) => {
    if (!data) return
    setFormData(data)

    const payload = {
      pan: data?.panNumber ?? '',
      dob: data?.dateOfBirth?.split?.('-')?.reverse?.()?.join?.('/') ?? ''
    };

    if (!payload?.pan || !payload?.dob) {
      toast.error('Please enter PAN and date of birth')
      return
    }

    try {
      const response = await initiateKyc(payload)?.unwrap?.()
      console.log('Initiate KYC response:', response)

      if (response?.status === "VALIDATED") {
        toast.success("PAN Verified Successfully");

        // If we already have kraDetails from initiate response, use it
        if (response?.kraDetails) {
          setPanResponseData({
            fullName: response?.kraDetails?.personalInformation?.name ?? '',
            panNumber: response?.kraDetails?.personalInformation?.panNumber ?? '',
            dateOfBirth: response?.kraDetails?.personalInformation?.dob ?? '',
            mobileNumber: response?.kraDetails?.contactInformation?.mobileNumber ?? '',
            emailAddress: response?.kraDetails?.contactInformation?.emailAddress ?? '',
            address: response?.kraDetails?.contactInformation?.correspondenceAddress?.addressLine1 ?? '',
            kycMasterId: response?.kycMasterId ?? '',
            occupation: response?.kraDetails?.financialInformation?.occupation ?? '',
            income: response?.kraDetails?.financialInformation?.income ?? ''
          });
          setIsModalOpen(true);
        } else {
          // Trigger getKraDetails API as fallback
          try {
            const kraResponse = await getKraDetails()?.unwrap?.();

            if (kraResponse?.success && kraResponse?.kraDetails) {
              const kraDetails = kraResponse?.kraDetails ?? {};
              setPanResponseData({
                fullName: kraDetails?.personalInformation?.name ?? '',
                panNumber: kraDetails?.personalInformation?.panNumber ?? '',
                dateOfBirth: kraDetails?.personalInformation?.dob ?? '',
                mobileNumber: kraDetails?.contactInformation?.mobileNumber ?? '',
                emailAddress: kraDetails?.contactInformation?.emailAddress ?? '',
                address: kraDetails?.contactInformation?.correspondenceAddress?.addressLine1 ?? '',
                kycMasterId: kraResponse?.kycMasterId ?? response?.kycMasterId ?? '',
                occupation: kraDetails?.financialInformation?.occupation ?? '',
                income: kraDetails?.financialInformation?.income ?? ''
              });
              setIsModalOpen(true);
            } else {
              // Fallback to initial response data if new API fails or returns empty
              setPanResponseData({
                fullName: response?.kraStatus?.name ?? '',
                panNumber: response?.kraStatus?.panNumber ?? '',
                dateOfBirth: response?.kraStatus?.dob ?? '',
                mobileNumber: response?.kraStatus?.mobileNumber ?? '',
                emailAddress: response?.kraStatus?.emailAddress ?? '',
                address: response?.kraStatus?.correspondenceAddress?.addressLine1 ?? '',
                kycMasterId: response?.kycMasterId ?? '',
                occupation: '',
                income: ''
              });
              setIsModalOpen(true);
            }
          } catch (error) {
            console.error("Error fetching KRA details:", error);
            // Fallback to kraStatus from initiateKyc
            setPanResponseData({
              fullName: response?.kraStatus?.name ?? '',
              panNumber: response?.kraStatus?.pan_number ?? '',
              dateOfBirth: response?.kraStatus?.dob ?? '',
              mobileNumber: response?.kraStatus?.mobile_number ?? '',
              emailAddress: response?.kraStatus?.email_address ?? '',
              address: response?.kraStatus?.correspondence_address?.address_line1 ?? '',
              kycMasterId: response?.kycMasterId ?? '',
              occupation: '',
              income: ''
            });
            setIsModalOpen(true);
          }
        }
      } else {
        navigate(ROUTES?.ONBOARDING_KYC, {
          state: {
            panNo: formData?.panNumber ?? data?.panNumber ?? '',
            dob: formData?.dateOfBirth ?? data?.dateOfBirth ?? ''
          }
        });
      }
    } catch (err) {
      console.error("KYC Initiation error:", err);
      toast.error(err?.data?.message ?? err?.message ?? "Something went wrong during verification.");
    }
  }

  // Confirmation modal handler
  const handleConfirmVerification = async () => {
    setIsConfirmationModalOpen(false)
    setIsFetchingModalOpen(true)

    // Show fetching modal for 3 seconds
    await new Promise(resolve => setTimeout(resolve, 3000))

    // Close fetching modal and show error modal
    setIsFetchingModalOpen(false)
    setIsErrorModalOpen(true)
  }

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false)
    setFormData(null)
  }

  // Modal handlers
  const handleModalClose = () => {
    setIsModalOpen(false)
    setFormData(null)
    setPanResponseData(null)
  }

  const handleModalProceed = async () => {
    const panNo = formData?.panNumber ?? panResponseData?.panNumber ?? ''
    const dobRaw = formData?.dateOfBirth ?? panResponseData?.dateOfBirth ?? ''
    const kycMasterId = panResponseData?.kycMasterId ?? ''

    if (!kycMasterId || !panNo || !dobRaw) {
      toast.error('Missing KYC details. Please verify PAN again.')
      return
    }

    try {
      const dob = dobRaw?.includes?.('-')
        ? dobRaw?.split?.('-')?.reverse?.()?.join?.('/') ?? dobRaw
        : dobRaw

      await completeKyc({
        kycMasterId,
        panNo,
        dob
      })?.unwrap?.()

      toast.success('KYC completed successfully')
      setIsModalOpen(false)

      // The backend fills the income slab during KYC. Ask for FATCA details
      // only when it comes back empty.
      let latestProfile = profileResponse
      try {
        const refreshedProfile = await refetchProfile?.()
        latestProfile = refreshedProfile?.data ?? profileResponse
      } catch (refreshError) {
        console.error('Failed to refresh profile after KYC:', refreshError)
      }

      if (!resolveIncomeSlab(latestProfile, panResponseData)) {
        setIsFatcaModalOpen(true)
        return
      }

      setFormData(null)
      setPanResponseData(null)
      navigate(ROUTES?.ONBOARDING_STATUS)
    } catch (error) {
      console.error('Complete KYC failed:', error)
      toast.error(error?.message ?? error?.data?.message ?? 'Failed to complete KYC')
    }
  }

  const handleFatcaModalClose = () => {
    setIsFatcaModalOpen(false)
  }

  const handleFatcaSubmit = async (values) => {
    if (!values) {
      toast.error('Please fill all required FATCA details')
      return
    }

    const payload = buildUpdateKycProfilePayload(values)

    if (!payload) {
      toast.error('Please fill all required FATCA details')
      return
    }

    try {
      await updateCustomerProfile(payload)?.unwrap?.()
      toast.success('Profile details saved successfully')
      setIsFatcaModalOpen(false)
      setFormData(null)
      setPanResponseData(null)
      navigate(ROUTES?.ONBOARDING_STATUS)
    } catch (error) {
      console.error('Update FATCA profile failed:', error)
      toast.error(error?.message ?? error?.data?.message ?? 'Failed to save profile details')
    }
  }

  const fatcaDefaultValues = useMemo(() => {
    const fatcaDetails = profileResponse?.data?.fatcaDetails ?? {}
    return {
      birthPlace:
        fatcaDetails?.placeOfBirth ??
        fatcaDetails?.birthPlace ??
        profileResponse?.profile?.placeOfBirth ??
        '',
      occupation:
        fatcaDetails?.occupation ??
        panResponseData?.occupation ??
        '',
      incomeSlab: resolveIncomeSlab(profileResponse, panResponseData)
    }
  }, [profileResponse, panResponseData])

  const handleUpdateContact = () => {
    console.log('Update contact details clicked')
    setIsModalOpen(false)
    setIsUpdateContactModalOpen(true)
  }

  const handleUpdateContactModalClose = () => {
    setIsUpdateContactModalOpen(false)
  }

  const handleUpdateContactProceed = async (selectedFields) => {
    if (!selectedFields) return

    const payload = {
      isMobileUpdate: selectedFields?.mobile ?? false,
      isEmailUpdate: selectedFields?.email ?? false,
      pan: panResponseData?.panNumber ?? '',
      dob: formData?.dateOfBirth?.split?.('-')?.reverse?.()?.join?.('/') ?? '',
    };

    try {
      await initiateMobileEmailUpdate(payload)?.unwrap?.();
      toast.success("Requests submitted successfully");
      setIsUpdateContactModalOpen(false);
      navigate(ROUTES?.ONBOARDING_STATUS);
    } catch (error) {
      console.error("Update KRA details failed:", error);
      toast.error(error?.message ?? error?.data?.message ?? "Failed to submit requests");
    }
  }

  const handleReKyc = async () => {
    try {
      const response = await reKyc()?.unwrap?.();
      console.log('Re-KYC response:', response);
      toast.success("Re-KYC initiated successfully");
      setIsModalOpen(false);
      navigate(ROUTES?.ONBOARDING_KYC, {
        state: {
          panNo: formData?.panNumber ?? '',
          dob: formData?.dateOfBirth ?? '',
          kraDetails: response?.result ?? null
        }
      });
    } catch (error) {
      console.error("Re-KYC initiation failed:", error);
      toast.error(error?.message ?? error?.data?.message ?? "Failed to initiate Re-KYC");
    }
  }

  // Error modal handlers
  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false)
    setFormData(null)
  }

  const handleRetry = async () => {
    console.log('Retrying PAN verification:', formData)
    // Close error modal
    setIsErrorModalOpen(false)

    // Show fetching modal
    setIsFetchingModalOpen(true)

    // Show fetching modal for 3 seconds
    await new Promise(resolve => setTimeout(resolve, 3000))

    // Close fetching modal and show success modal
    setIsFetchingModalOpen(false)
    setIsModalOpen(true)
  }

  const handleContactRM = () => {
    console.log('Contacting Relationship Manager')
    // Handle contact RM logic
    setIsErrorModalOpen(false)
    setFormData(null)
  }

  // Return/rendering
  return (
    <Container sx={{}}>
      <PanCardFormComponent
        // Header configuration
        title={PAN_CARD_FORM_CONFIG?.title}
        subtitle={PAN_CARD_FORM_CONFIG?.subtitle}

        // Form section configuration
        sectionTitle={PAN_CARD_FORM_CONFIG?.sectionTitle}
        sectionDescription={PAN_CARD_FORM_CONFIG?.sectionDescription}

        // Button configuration
        buttonText={PAN_CARD_FORM_CONFIG?.buttonText}

        // Note configuration
        noteText={PAN_CARD_FORM_CONFIG?.noteText}
        showNote={PAN_CARD_FORM_CONFIG?.showNote}

        // Event handlers
        onSubmit={handleSubmit?.(handleFormSubmit)}
        onBack={() => navigate(ROUTES?.ONBOARDING_STATUS)}
        isMobile={isMobile}

        // State
        buttonLoading={!!isKycLoading || !!isKraFetching}
        buttonDisabled={!isValid}
      >
        {/* PAN Number Input */}
        <Stack spacing={4} direction="column">
          <Controller
            name="panNumber"
            control={control}
            rules={VALIDATION_RULES?.panNumber}
            render={({ field }) => (
              <AppTextFieldComponent
                {...(field ?? {})}
                label="PAN Card Number"
                placeholder="Enter Pan Number"
                onChange={(e) => field?.onChange?.(e?.target?.value?.toUpperCase?.() ?? '')}
                error={!!errors?.panNumber}
                helperText={errors?.panNumber?.message}
                required
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  maxLength: 10,
                }}
                sx={{ ...INPUT_STYLES, maxWidth: '400px' }}
              />
            )}
          />


          {/* Date of Birth Input */}
          <Box sx={{ width: '100%' }}>
            <Controller
              name="dateOfBirth"
              control={control}
              rules={VALIDATION_RULES?.dateOfBirth}
              render={({ field }) => (
                <DatePicker
                  label="Date Of Birth"
                  placeholder="Select Date of Birth"
                  value={field?.value ?? ''}
                  maxDate={new Date()}
                  onChange={(e) => field?.onChange?.(e?.target?.value ?? '')}
                  error={!!errors?.dateOfBirth}
                  required
                  selectSx={{
                    maxWidth: '400px',
                    height: '56px',
                    backgroundColor: '#2A2D3A',
                    color: 'white',
                    minHeight: '56px',
                    boxShadow: 'none'
                  }}
                />
              )}
            />
            {errors?.dateOfBirth && (
              <Box sx={{
                color: '#d32f2f',
                fontSize: '12px',
                marginTop: '8px',
              }}>
                {errors?.dateOfBirth?.message}
              </Box>
            )}
          </Box>

        </Stack>
        <Box sx={{ height: theme?.spacing?.(3) }} />
      </PanCardFormComponent>

      {/* KYC Information Modal */}
      <PanKycInfoModal
        open={!!isModalOpen}
        onClose={handleModalClose}
        onConfirm={handleModalProceed}
        onUpdateContact={handleUpdateContact}
        onReKyc={handleReKyc}
        data={panResponseData ?? {
          fullName: formData?.fullName ?? '',
          panNumber: formData?.panNumber ?? '',
          dateOfBirth: formData?.dateOfBirth ?? '',
          mobileNumber: '',
          emailAddress: '',
          address: ''
        }}
      />

      {/* FATCA details after KYC complete */}
      <FatcaDetailsModal
        open={!!isFatcaModalOpen}
        onClose={handleFatcaModalClose}
        onSubmit={handleFatcaSubmit}
        loading={!!isUpdatingProfile}
        defaultValues={fatcaDefaultValues ?? null}
      />

      {/* Update Contact Details Modal */}
      <UpdateContactDetailsModal
        open={!!isUpdateContactModalOpen}
        onClose={handleUpdateContactModalClose}
        onProceed={handleUpdateContactProceed}
        mobileNumber={panResponseData?.mobileNumber ?? ''}
        emailAddress={panResponseData?.emailAddress ?? ''}
      />

      {/* PAN Fetch Error Modal */}
      <PanFetchErrorModal
        open={!!isErrorModalOpen}
        onClose={handleErrorModalClose}
        onRetry={handleRetry}
        onContactRM={handleContactRM}
        title="Fetch failed"
        message="PAN details unavailable. Please retry or enter manually."
        retryText="Retry"
        contactText="Contact RM"
      />

      {/* Confirmation Modal */}
      <ConfirmationModel
        open={!!isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={handleConfirmVerification}
        title="Verify PAN Card"
        description="To proceed, we need your consent to verify your PAN details with the issuing authority.
This helps us confirm your identity and ensure a secure onboarding process."
        confirmText="I Agree & Verify"
        cancelText="Cancel"
        icon={<QuestionMarkIcon />}
        iconPropsStyle={{
          backgroundColor: '#3F3336',
          color: '#F2C94C'
        }}
      />

      {/* Fetching Details Modal */}
      <FetchingDetailModal
        open={!!isFetchingModalOpen}
        isMobile={isMobile}
        title="Fetching Details"
        description="Your PAN Card Details are being fetched."
      />

      {/* Loading Modal - Blocks functionality during PAN verification / KYC complete */}
      <LoadingModal
        open={!!isKycLoading || !!isKraFetching || !!isCompleteKycLoading}
        title={isCompleteKycLoading ? 'Completing KYC' : 'Verifying PAN Card'}
        description={
          isCompleteKycLoading
            ? 'Please wait while we complete your KYC verification.'
            : 'Please wait while we verify your PAN card details. This may take a few moments.'
        }
      />
    </Container>
  )
}

export default PanCardContainer

